import {
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Divider,
    Link,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Theme
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import React, { ChangeEvent } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { TableEditBar } from '../../../components/TableEditBar';
import { OverdueWcvDocument, WcvDocument } from '../../../graphql/documents.datatypes';
import { formatDate } from '../../../utils/formatters';
import { WcvType } from '../../../graphql/logs.datatypes';
import { getHost } from '../../../graphql/GraphQlProvider';
import uploadFile, { UploadFileVars } from '../../../graphql/uploadFile.api';
import { useSnackbar } from 'notistack';
import createDocument, { CreateDocumentInput } from '../createDocument.api';
import updateDocument, { UpdateDocumentInput } from '../updateDocument.api';
import { getMe } from '../../../graphql/auth';
import moment from 'moment';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { red } from '@material-ui/core/colors';
import getOverdueGroupMemberDocuments from '../getOverdueGroupMemberDocuments.api';
import { LoadingOverlay } from '../../../components/LoadingOverlay';
import { checkIsAdmin } from '../../../utils/checkIsAdmin';
import { Page } from '../../../components/Page';
import PageHeader from '../../../components/PageHeader';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(3)
    },
    container: {
        marginTop: theme.spacing(3),
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    content: {
        padding: 0
    },
    inner: {
        [theme.breakpoints.up('md')]: {
            minWidth: 700,
        },
    },
    nameCell: {
        display: 'flex',
        alignItems: 'center'
    },
    avatar: {
        height: 42,
        width: 42,
        marginRight: theme.spacing(1)
    },
    actions: {
        padding: theme.spacing(1),
        justifyContent: 'flex-end'
    },
    linkButton: {
        marginRight: theme.spacing(1),
    },
    chipOverdue: {
        backgroundColor: red[200],
        marginLeft: theme.spacing(1),
    },
}));

export const OverdueDocumentListContent = (props: {
    className?: string,
    overdueDocuments: OverdueWcvDocument[],
    isAdmin?: boolean,
    title?: string
}) => {
    const {className, overdueDocuments, isAdmin, title, ...rest} = props;
    const classes = useStyles();
    const {enqueueSnackbar} = useSnackbar();
    const meData = getMe();
    const [sendUploadFile] = uploadFile();
    const [sendCreateDocument] = createDocument();
    const [sendUpdateDocument] = updateDocument();

    const handleVerifyDocument = (document: WcvDocument,) => {
        const sanitisedData: UpdateDocumentInput = {
            data: {
                __typename: undefined,
                user: undefined,
                targetResource: undefined,
                targetGroup: undefined,
                targetGroupMember: undefined,
                completed_documents: undefined,
                overdueGroupMembers: undefined,

                id: document.id,
                verified: true,
            }
        };

        sendUpdateDocument({variables: sanitisedData})
            .then(() => {
                enqueueSnackbar('Document Verified.', {variant: 'success'});
            })
            .catch(() => {
                enqueueSnackbar('Verify Document Failed!', {variant: 'error'});
            });
    };

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>, overdueDocument: OverdueWcvDocument, memberDoc?: WcvDocument) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            if (file.size <= 5000000) {
                const sanitisedData: UploadFileVars = {
                    data: {
                        file,
                    },
                };
                sendUploadFile({variables: sanitisedData})
                    .then((response) => {
                        enqueueSnackbar('File Upload Success!', {variant: 'success'});
                        const fileUrl = response.data!.uploadFile;
                        handleUploadDocument(fileUrl, overdueDocument, memberDoc);
                    })
                    .catch(() => {
                        enqueueSnackbar('File Upload Failed!', {variant: 'error'});
                    });
            } else {
                enqueueSnackbar('Document Too Large. 5MB limit.', {variant: 'error'});
            }
        }
    };

    const handleUploadDocument = (newDocumentUrl: string, overdueDocument: OverdueWcvDocument, memberDoc?: WcvDocument) => {
        console.log(memberDoc)
        if (memberDoc) {
            const sanitisedData: UpdateDocumentInput = {
                data: {
                    __typename: undefined,
                    user: undefined,
                    targetResource: undefined,
                    targetGroup: undefined,
                    targetGroupMember: undefined,
                    completed_documents: undefined,
                    overdueGroupMembers: undefined,

                    id: memberDoc.id,
                    url: newDocumentUrl,
                    verified: false,
                }
            };

            sendUpdateDocument({variables: sanitisedData})
                .then(() => {
                    enqueueSnackbar('Document Updated.', {variant: 'success'});
                })
                .catch(() => {
                    enqueueSnackbar('Update Document Failed!', {variant: 'error'});
                });

        } else {
            const sanitisedData: CreateDocumentInput = {
                data: {
                    __typename: undefined,
                    id: undefined,
                    completed_documents: undefined,
                    overdueGroupMembers: undefined,
                    user_id: meData.data!.me!.id!,
                    type: WcvType.GROUP_MEMBER,
                    type_id: overdueDocument.groupMember.id,
                    parent_document_id: overdueDocument.document.id,
                    title: overdueDocument.document.title,
                    description: overdueDocument.document.description,
                    available_for_download: false,
                    has_completion_date: false,
                    verified: false,
                    url: newDocumentUrl,
                    tour_role: -1,
                }
            };

            sendCreateDocument({variables: sanitisedData})
                .then(() => {
                    enqueueSnackbar('Document Saved.', {variant: 'success'});
                })
                .catch(() => {
                    enqueueSnackbar('Save Document Failed!', {variant: 'error'});
                });
        }
    };

    return (
        <div {...rest} className={className}>
            <Card className={classes.container}>
                <CardHeader title={title ? title : 'Overdue Documents'}/>
                <Divider/>
                <CardContent className={classes.content}>
                    <PerfectScrollbar>
                        <div className={classes.inner}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Title</TableCell>
                                        <TableCell>Group</TableCell>
                                        <TableCell>Group Member</TableCell>
                                        <TableCell style={{width: 125}}>Complete By</TableCell>
                                        <TableCell style={{width: 100}}>Completed?</TableCell>
                                        {isAdmin && (<TableCell style={{width: 100}}>Verified?</TableCell>)}
                                        <TableCell style={{width: 250}}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {overdueDocuments.map((overdueDocument, i) => {
                                        const document = overdueDocument.document;
                                        const groupMember = overdueDocument.groupMember;
                                        const memberDoc = document.completed_documents && document.completed_documents.find(doc => doc.type === WcvType.GROUP_MEMBER && doc.type_id === groupMember.id);
                                        const overdue = document.has_completion_date && (!memberDoc && moment(document.completion_date).isSameOrBefore(new Date()));

                                        return (
                                            <TableRow hover key={i}>
                                                <TableCell>{document.title}</TableCell>
                                                <TableCell>{groupMember.group && groupMember.group.name}</TableCell>
                                                <TableCell>{groupMember.first_name + ' ' + groupMember.last_name}</TableCell>
                                                <TableCell>
                                                    {document.has_completion_date && formatDate(document.completion_date)}
                                                    {overdue && (
                                                        <Chip icon={<ErrorOutlineIcon/>} label="overdue"
                                                              className={classes.chipOverdue} size="small"/>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {memberDoc ? (
                                                        <Link href={getHost() + memberDoc.url} target="_blank">
                                                            <Button variant="contained"
                                                                    className={classes.linkButton}>View</Button>
                                                        </Link>
                                                    ) : 'no'}
                                                </TableCell>
                                                {isAdmin && (
                                                    <TableCell>
                                                        {memberDoc ?
                                                            memberDoc.verified ? 'yes' :
                                                                'no' + isAdmin ? (
                                                                    <Button variant="contained"
                                                                            className={classes.linkButton}
                                                                            onClick={() => handleVerifyDocument(memberDoc)}
                                                                    >Verify</Button>
                                                                ) : ''
                                                            : '-'}
                                                    </TableCell>
                                                )}
                                                <TableCell>
                                                    <Link href={getHost() + document.url} target="_blank">
                                                        <Button variant="contained"
                                                                className={classes.linkButton}>Download</Button>
                                                    </Link>

                                                    <form style={{display: 'inline'}}>

                                                        <label htmlFor={'file_upload[' + document.id + '_'+ groupMember.id +']'}>
                                                            <Button component="span" variant="contained"
                                                                    className={classes.linkButton}>Upload</Button>
                                                        </label>

                                                        <input
                                                            accept="application/pdf,image/*"
                                                            style={{display: 'none'}}
                                                            id={'file_upload[' + document.id + '_'+ groupMember.id +']'}
                                                            multiple
                                                            type="file"
                                                            onChange={event => handleFileChange(event, overdueDocument, memberDoc)}
                                                            value={''}
                                                        />
                                                    </form>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    </PerfectScrollbar>
                </CardContent>
            </Card>
            <TableEditBar selected={[]}/>
        </div>
    );
};

const overdueDocumentsQuery = `
    document {
        id
        parent_document_id
        user_id
        type
        type_id
        title
        description
        url
        has_completion_date
        completion_date
    }

    groupMember {
        id
        first_name
        last_name

        group {
            id
            name
        }
    }
`;

export const OverdueDocumentListLoad = (props: { groupId?: number }) => {
    const overdueDocumentsData = getOverdueGroupMemberDocuments(props.groupId, overdueDocumentsQuery);
    const overdueDocuments = !overdueDocumentsData.loading && overdueDocumentsData.data && overdueDocumentsData.data.overdueGroupMemberDocuments;

    const isAdmin = checkIsAdmin();

    return (
        <React.Fragment>
            <LoadingOverlay loading={overdueDocumentsData.loading}>
                <OverdueDocumentListContent overdueDocuments={overdueDocuments ? overdueDocuments : []}
                                            isAdmin={isAdmin}/>
            </LoadingOverlay>
        </React.Fragment>
    );
};

export const OverdueDocumentList = () => {
    const classes = useStyles();
    const title = 'My Groups Overdue Documents'

    return (
        <Page className={classes.root} title={title}>
            <PageHeader section="groups" title={title}/>
            <OverdueDocumentListLoad/>
        </Page>
    );
};

export default OverdueDocumentList;
